<template>
  <div class="card mb-25 border-0 rounded-0 bg-white add-user-card">
    <div class="card-body p-15 p-sm-20 p-md-25 p-lg-30 letter-spacing">
      <Form
        ref="clientAffecterForm"
        @submit="editClientAffecter"
        :validation-schema="clientAffecterSchema"
        :initial-values="initialValues"
      >
        <div class="row">
          <div class="col-md-12 mb-3">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Utilisateur <span class="text-danger">*</span>
              </label>
              <Field name="user" v-slot="{ field }">
                <Multiselect
                  v-model="field.value"
                  :options="userOptions"
                  :searchable="true"
                  track-by="label"
                  label="label"
                  placeholder="Sélectionner l'Utilisateur"
                  v-bind="field"
                />
              </Field>
              <ErrorMessage name="user" class="text-danger" />
            </div>
          </div>
          <div class="col-md-12 mb-3">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Client <span class="text-danger">*</span>
              </label>
              <Field name="client" v-slot="{ field }">
                <Multiselect
                  v-model="field.value"
                  :options="clientOptions"
                  :searchable="true"
                  track-by="label"
                  mode="tags"
                  label="label"
                  placeholder="Sélectionner le Client"
                  v-bind="field"
                />
              </Field>
              <ErrorMessage name="client" class="text-danger" />
            </div>
          </div>
          <div class="col-md-12 mt-3">
            <div class="d-flex align-items-center">
              <button class="btn btn-success me-3" type="submit">
                Modifier l'affectation
              </button>
              <router-link to="/clientAffecter/liste-clientAffecter" class="btn btn-danger">
                <i class="fa fa-trash-o lh-1 me-1 position-relative top-2"></i>
                <span class="position-relative">Annuler</span>
              </router-link>
            </div>
          </div>
        </div>
      </Form>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from 'vue';
import { Form, Field, ErrorMessage } from 'vee-validate';
import ApiService from '@/services/ApiService';
import * as Yup from 'yup';
import { error, success } from '@/utils/utils';
import { useRouter, useRoute } from 'vue-router';
import Multiselect from '@vueform/multiselect';
import { ClientAffecter } from '@/models/ClientAffecter';

export default defineComponent({
  name: 'EditClientAffecter',
  components: {
    Form,
    Field,
    ErrorMessage,
    Multiselect,
  },
  setup() {
    const clientAffecterSchema = Yup.object().shape({
      user: Yup.string().required("L'utilisateur est obligatoire"),
      client: Yup.array().min(1, "Au moins un client est obligatoire").required("Le client est obligatoire"),
    });

    const clientAffecterForm = ref<any>(null);
    const router = useRouter();
    const route = useRoute();
    const clientOptions = ref<Array<{ value: string; label: string }>>([]);
    const userOptions = ref<Array<{ value: string; label: string }>>([]);
    const initialValues = ref<{ id?: number; user?: string; client?: string[] }>({});

    const getAllClients = async () => {
      try {
        const response = await ApiService.get('/all/clients');
        const clientData = response.data.data;
        clientOptions.value = clientData.map((client: any) => ({
          value: client.id.toString(),
          label: client.nomClient && client.prenomClient
            ? `${client.nomClient} ${client.prenomClient}`
            : `${client.raisonSociale}`,
        }));
      } catch (err) {
        // error('Erreur lors de la récupération des clients');
      }
    };

    const getAllUsers = async () => {
      try {
        const response = await ApiService.get('/all/users');
        const userData = response.data.data;
        userOptions.value = userData.map((user: any) => ({
          value: user.id.toString(),
          label: `${user.nom} ${user.prenom}`,
        }));
      } catch (err) {
        // error('Erreur lors de la récupération des utilisateurs');
      }
    };

    const getClientAffecter = async (id: number) => {
      try {
        const { data } = await ApiService.get(`/clientAffecter/${id}`);
        console.log('Données récupérées:', data.data);

        const affecter = data.data;
        initialValues.value = {
          id: affecter.id,
          user: affecter.user?.id?.toString() || '',
          client: Array.isArray(affecter.client)
            ? affecter.client.map((c: any) => c.id.toString())
            : affecter.client?.id
              ? [affecter.client.id.toString()]
              : [],
        };

        if (clientAffecterForm.value) {
          clientAffecterForm.value.setValues(initialValues.value);
          console.log('Valeurs assignées au formulaire:', initialValues.value);
        }
      } catch ({ response }) {
        error(response?.data?.message || "Erreur lors de la récupération des données");
      }
    };

    const editClientAffecter = async (values: any, { resetForm }: any) => {
      console.log('Valeurs envoyées à l’API:', values);
      try {
        const payload = {
          id: values.id,
          user: values.user,
          client: values.client,
        };
        const { data } = await ApiService.put(`/clientAffecter/${values.id}`, payload);
        if (data.code === 200) {
          success(data.message);
          resetForm();
          router.push({ name: 'ListeClientAffecter' });
        }
      } catch ({ response }) {
        error(response?.data?.message || "Erreur lors de la modification");
      }
    };

    onMounted(async () => {
      await Promise.all([getAllClients(), getAllUsers()]);
      if (route.params.id) {
        await getClientAffecter(parseInt(route.params.id as string));
      }
    });

    return {
      clientAffecterSchema,
      editClientAffecter,
      clientAffecterForm,
      clientOptions,
      userOptions,
      initialValues,
    };
  },
});
</script>